/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useEffect, useMemo, useState } from "react";
import { Modal, Box, Typography, InputBase, useTheme, Tooltip } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import RadioButtonCheckedRoundedIcon from "@mui/icons-material/RadioButtonCheckedRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { AI_ACCENT } from "../model/tokens";
import { listChats, deleteChat } from "../model/assistantHistory";
import { useAssistant } from "../AssistantContext";

/** Normalise a stored chat row into the list-item shape this modal renders. */
const toItem = (c) => ({
  assistantChatId: c.id,
  title: c.title || "Untitled conversation",
  updatedAt: c.updated_at ? Date.parse(c.updated_at) : c.created_at ? Date.parse(c.created_at) : Date.now(),
});

const DAY = 86400000;
const VIOLET = "#7C5CFC";

/** Bucket a timestamp into Today / This week / This month / Earlier relative to now. */
function bucketOf(ts) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (ts >= startOfToday) return "Today";
  if (ts >= startOfToday - 6 * DAY) return "This week";
  if (ts >= startOfToday - 30 * DAY) return "This month";
  return "Earlier";
}
const ORDER = ["Today", "This week", "This month", "Earlier"];

/**
 * Conversation history — search, filters, time-grouped list.
 */
export default function HistoryModal({ open, onClose }) {
  const theme = useTheme();
  const { newChat, loadChat } = useAssistant();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState([]); // ["pulse"], ["guide"], or both
  const [items, setItems] = useState([]);

  // Fetch the user's stored conversations each time the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listChats()
      .then((chats) => {
        if (!cancelled) setItems(chats.map(toItem));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const toggleFilter = (f) =>
    setFilters((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = items.filter((it) => {
      if (q && !it.title.toLowerCase().includes(q)) return false;
      if (filters.length && !filters.includes(it.type)) return false;
      return true;
    });
    const by = {};
    filtered
      .slice()
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .forEach((it) => {
        const b = bucketOf(it.updatedAt);
        (by[b] = by[b] || []).push(it);
      });
    return ORDER.filter((k) => by[k]).map((k) => ({ label: k, items: by[k] }));
  }, [items, query, filters]);

  const startNew = () => {
    newChat();
    onClose();
  };
  const openChat = (item) => {
    if (item?.assistantChatId) loadChat(item.assistantChatId);
    onClose();
  };
  const remove = (e, id) => {
    e.stopPropagation();
    // Optimistic removal; the API soft-deletes server-side.
    setItems((prev) => prev.filter((it) => it.assistantChatId !== id));
    deleteChat(id).catch(() => {});
  };

  const chip = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.25,
    py: 0.5,
    borderRadius: "999px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    border: `1px solid ${active ? AI_ACCENT : theme.palette.border.color}`,
    color: active ? AI_ACCENT : theme.palette.text.color,
    backgroundColor: "transparent",
  });

  return (
    <Modal open={open} onClose={onClose} aria-label="Conversation history">
      <Box
        sx={{
          position: "absolute",
          top: "14%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(560px, calc(100vw - 32px))",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.secondary.main,
          border: `1px solid ${theme.palette.border.color}`,
          borderRadius: "16px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        {/* Search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.border.color}`,
          }}
        >
          <SearchRoundedIcon sx={{ color: theme.palette.secondaryText.color, fontSize: 20 }} />
          <InputBase
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations…"
            sx={{ flex: 1, fontSize: 15, color: theme.palette.text.color }}
          />
          <Box role="button" aria-label="Close" onClick={onClose} sx={{ display: "inline-flex", cursor: "pointer", color: theme.palette.secondaryText.color }}>
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ px: 2, pt: 1.5, display: "flex", gap: 1 }}>
          <Box sx={chip(filters.includes("pulse"))} onClick={() => toggleFilter("pulse")}>
            <RadioButtonCheckedRoundedIcon sx={{ fontSize: 15, color: VIOLET }} /> Pulses
          </Box>
          <Box sx={chip(filters.includes("guide"))} onClick={() => toggleFilter("guide")}>
            <MenuBookRoundedIcon sx={{ fontSize: 15, color: AI_ACCENT }} /> Guides
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ overflow: "auto", px: 1, py: 1, mt: 0.5 }}>
          <Box
            role="button"
            onClick={startNew}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.5,
              py: 1,
              borderRadius: "10px",
              cursor: "pointer",
              color: theme.palette.text.color,
              fontWeight: 500,
              "&:hover": { backgroundColor: theme.palette.tertiary.main },
            }}
          >
            <EditNoteRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondaryText.color }} />
            New conversation
          </Box>

          {groups.length === 0 && (
            <Typography sx={{ px: 1.5, py: 3, textAlign: "center", color: theme.palette.secondaryText.color, fontSize: 14 }}>
              No conversations found.
            </Typography>
          )}

          {groups.map((g) => (
            <Box key={g.label}>
              <Typography
                sx={{
                  px: 1.5,
                  pt: 1.5,
                  pb: 0.5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.palette.secondaryText.color,
                }}
              >
                {g.label}
              </Typography>
              {g.items.map((it) => (
                <Box
                  key={it.assistantChatId}
                  role="button"
                  onClick={() => openChat(it)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: "10px",
                    cursor: "pointer",
                    color: theme.palette.text.color,
                    "&:hover": { backgroundColor: theme.palette.tertiary.main },
                    "&:hover .vcs-del": { opacity: 1 },
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 14,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {it.title}
                  </Typography>
                  {it.type === "pulse" && (
                    <RadioButtonCheckedRoundedIcon sx={{ fontSize: 16, color: VIOLET, flexShrink: 0 }} />
                  )}
                  {it.type === "guide" && (
                    <MenuBookRoundedIcon sx={{ fontSize: 16, color: AI_ACCENT, flexShrink: 0 }} />
                  )}
                  <Tooltip title="Delete" arrow>
                    <Box
                      className="vcs-del"
                      role="button"
                      aria-label="Delete conversation"
                      onClick={(e) => remove(e, it.assistantChatId)}
                      sx={{
                        display: "inline-flex",
                        opacity: 0,
                        transition: "opacity 0.15s",
                        color: theme.palette.secondaryText.color,
                        "&:hover": { color: theme.palette.error?.main || "#CF4E36" },
                      }}
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Modal>
  );
}
